<script setup lang="ts">
/**
 * Dispatcher for `tool`-type parts. Resolves the shared cross-runtime manifest
 * (the SAME `chat-tools.manifest.json` the backend tool registry embeds, exposed
 * here via the building-blocks `#bb` alias — a single source of truth) to decide
 * whether a tool call is an interactive building block that should render as an
 * inline answerable control.
 *
 *  - Interactive + available + carrying input args → the matching inline block;
 *    on answer it builds the tool's `result` shape from the manifest `valuePath`
 *    and emits it up to be POSTed via `SubmitToolResult`.
 *  - Candidate assessment tools never render model-supplied block data. They
 *    resolve `input.id` to an existing resource block and the canonical renderer
 *    persists with `useAnswers` before emitting the result that settles the tool.
 *  - Anything else (display tools, editor tools, or a resolved block reloaded
 *    without its original args) → the read-only diagnostic `ToolPart`.
 */
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
// `#bb` is registered by the building-blocks module, which both host apps load
// before the chat module. Importing the raw JSON keeps the manifest authoritative
// without forking its data into the chat module.
import manifest from "#bb/lib/chat-tools.manifest.json";
import Renderer from "#bb/components/renderer/index.vue";
import {
  CandidateAssessmentBlocksKey,
  isAssessmentAnswerComponentId,
  normaliseAssessmentToolResultValue,
} from "#bb/lib/assessmentAnswers";
import { isTerminalToolState } from "../../lib/tool-visibility";
import type { ChatSurface, ToolUiPart } from "../../types/chat";
import ToolPart from "../parts/tool-part.vue";
import ConfirmCard from "../blocks/confirm-card.vue";
import RadioGroupBlock from "../blocks/radio-group.vue";
import CheckboxGroupBlock from "../blocks/checkbox-group.vue";
import SelectInputBlock from "../blocks/select-input.vue";
import TextInputBlock from "../blocks/text-input.vue";
import NumberInputBlock from "../blocks/number-input.vue";
import BarChartBlock from "../blocks/bar-chart.vue";
import KpiBlock from "../blocks/kpi.vue";

interface ManifestAnswer {
  type: string;
  valuePath: string;
}
interface ManifestTool {
  name: string;
  kind: string;
  status: string;
  componentId?: string | null;
  title?: string | null;
  confirmBeforeWrite?: boolean;
  answer?: ManifestAnswer | null;
}

const props = withDefaults(
  defineProps<{
    tool: ToolUiPart;
    /** Fallback text (e.g. an error message) for the diagnostic renderer. */
    text?: string;
    /** Disable answering (e.g. while a reply is streaming). */
    disabled?: boolean;
    /** Host surface; candidate resolves assessment tools to existing resource blocks. */
    surface?: ChatSurface;
    /** Show terminal generic tool input/output payload cards. */
    showToolDetails?: boolean;
  }>(),
  { disabled: false, surface: "admin", showToolDetails: false },
);

const emit = defineEmits<{
  (e: "submit", payload: { invocationId: string; toolName: string; result: Record<string, unknown> }): void;
  (e: "cancel", payload: { invocationId: string; toolName: string }): void;
  (
    e: "confirm",
    payload: { invocationId: string; toolName: string; approve: boolean; summary?: string },
  ): void;
}>();

const { t } = useI18n({ useScope: "local" });
const { getContentById, contentState } = useContentManager();
const answerableBlocks = inject(CandidateAssessmentBlocksKey, null);

/** The inline block component for each interactive tool name. */
const BLOCK_COMPONENTS: Record<string, unknown> = {
  render_radio_group: RadioGroupBlock,
  render_checkbox_group: CheckboxGroupBlock,
  render_select_input: SelectInputBlock,
  render_text_input: TextInputBlock,
  render_number_input: NumberInputBlock,
};

const tools = ((manifest as { tools?: ManifestTool[] }).tools ?? []);
const byName = new Map<string, ManifestTool>(tools.map((t) => [t.name, t]));

/**
 * Display-only blocks (charts, KPI tiles) render the model-supplied data on BOTH
 * surfaces and regardless of state: the backend settles the call automatically,
 * so these parts arrive already `output-available` and simply stay visible in the
 * transcript.
 */
const DISPLAY_COMPONENTS: Record<string, unknown> = {
  render_bar_chart: BarChartBlock,
  render_kpi: KpiBlock,
};

const descriptor = computed(() => byName.get(props.tool.name));

const input = computed<Record<string, unknown>>(() =>
  props.tool.input && typeof props.tool.input === "object"
    ? (props.tool.input as Record<string, unknown>)
    : {},
);

const hasInput = computed(() => Object.keys(input.value).length > 0);
const candidateBlockId = computed(() =>
  typeof input.value.id === "string" ? input.value.id.trim() : "",
);
const candidateBlock = computed<Record<string, unknown> | null>(() =>
  candidateBlockId.value ? getContentById(candidateBlockId.value) : null,
);
const hasCandidateResource = computed(
  () => contentState.value.length > 0 || (answerableBlocks?.value.length ?? 0) > 0,
);
const isCandidateAssessmentTool = computed(() => {
  const desc = descriptor.value;
  return (
    props.surface === "candidate" &&
    !!desc &&
    desc.kind === "interactive" &&
    desc.status === "available" &&
    !!desc.componentId &&
    isAssessmentAnswerComponentId(desc.componentId) &&
    hasInput.value
  );
});

const candidateRendererAvailable = computed(() => {
  if (!isCandidateAssessmentTool.value) {
    return false;
  }
  const desc = descriptor.value;
  const blockComponentId = candidateBlock.value?.componentId;
  return (
    !!candidateBlockId.value &&
    hasCandidateResource.value &&
    candidateBlock.value?.hidden !== true &&
    !!desc?.componentId &&
    isAssessmentAnswerComponentId(blockComponentId as string | undefined) &&
    blockComponentId === desc.componentId
  );
});

const candidateFallbackText = computed(() => {
  if (!isCandidateAssessmentTool.value || candidateRendererAvailable.value) {
    return "";
  }
  if (!candidateBlockId.value) {
    return t("missingBlockId");
  }
  if (!hasCandidateResource.value) {
    return t("resourceUnavailable");
  }
  if (!candidateBlock.value) {
    return t("blockNotFound", { id: candidateBlockId.value });
  }
  if (candidateBlock.value.componentId !== descriptor.value?.componentId) {
    return t("blockMismatch");
  }
  return t("notAnswerable");
});

/**
 * Render the inline interactive control only when the manifest marks this tool
 * interactive + available, a renderer exists, and the original args are present.
 * A resolved block reloaded from history (no input args) falls back to ToolPart.
 */
const blockComponent = computed(() => {
  if (props.surface === "candidate") {
    return null;
  }
  const desc = descriptor.value;
  if (!desc || desc.kind !== "interactive" || desc.status !== "available") {
    return null;
  }
  if (!hasInput.value) {
    return null;
  }
  return BLOCK_COMPONENTS[desc.name] ?? null;
});

/** Display-only block (chart/KPI): rendered on any surface whenever its input args are present. */
const displayComponent = computed(() => {
  const desc = descriptor.value;
  if (!desc || desc.kind !== "display" || desc.status !== "available") {
    return null;
  }
  if (!hasInput.value) {
    return null;
  }
  return DISPLAY_COMPONENTS[desc.name] ?? null;
});

/**
 * Confirm-before-write editor tools (e.g. `resource_persist`) render an approve/
 * reject card. The backend signals this by parking the call in the
 * `approval-requested` state; we keep showing the card (now read-only) for the
 * resolved states so the transcript reflects the outcome.
 */
const isConfirmTool = computed(() => {
  const desc = descriptor.value;
  return !!desc && desc.confirmBeforeWrite === true && desc.status === "available";
});

const confirmTitle = computed(() => descriptor.value?.title ?? undefined);

function onSubmit(raw: unknown) {
  const valuePath = descriptor.value?.answer?.valuePath;
  if (!valuePath) {
    return;
  }
  emit("submit", {
    invocationId: props.tool.invocationId,
    toolName: props.tool.name,
    result: { [valuePath]: raw },
  });
}

function onCandidateAnswer(raw: unknown) {
  const desc = descriptor.value;
  const valuePath = desc?.answer?.valuePath;
  if (!valuePath) {
    return;
  }
  emit("submit", {
    invocationId: props.tool.invocationId,
    toolName: props.tool.name,
    result: {
      [valuePath]: normaliseAssessmentToolResultValue(desc.componentId, raw),
    },
  });
}

/**
 * True while the tool is still awaiting an answer. The bare building block is shown only while
 * pending; once the answer is submitted (the tool resolves to output-available/denied/error) the part
 * collapses into the read-only AI Elements `Tool` card (`ToolPart`) for transcript history.
 */
const isPending = computed(() => props.tool.state === "input-available");
const isTerminal = computed(() => isTerminalToolState(props.tool.state));

/** True while the block is rendered and still awaiting an answer (so it can be skipped). */
const canSkip = computed(
  () =>
    (blockComponent.value !== null || candidateRendererAvailable.value || !!candidateFallbackText.value) &&
    !props.disabled &&
    props.tool.state === "input-available",
);

function onCancel() {
  emit("cancel", {
    invocationId: props.tool.invocationId,
    toolName: props.tool.name,
  });
}

</script>

<template>
  <ConfirmCard
    v-if="isConfirmTool"
    :tool="tool"
    :title="confirmTitle"
    :disabled="disabled"
    @confirm="(payload) => emit('confirm', payload)"
  />
  <component v-else-if="displayComponent" :is="displayComponent" :input="input" />
  <div v-else-if="candidateRendererAvailable && isPending" class="space-y-1.5">
    <Renderer
      :id="candidateBlockId"
      :disabled="disabled || tool.state !== 'input-available'"
      @content-update="onCandidateAnswer"
    />
    <div v-if="canSkip" class="flex justify-end">
      <Button type="button" variant="ghost" size="sm" class="text-muted-foreground" @click="onCancel">
        {{ t("skip") }}
      </Button>
    </div>
  </div>
  <div
    v-else-if="candidateFallbackText && isPending"
    class="not-prose space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
    data-slot="chat-block-unanswerable"
  >
    <p>{{ candidateFallbackText }}</p>
    <div v-if="canSkip" class="flex justify-end">
      <Button type="button" variant="ghost" size="sm" class="text-amber-950/70 hover:text-amber-950" @click="onCancel">
        {{ t("skip") }}
      </Button>
    </div>
  </div>
  <div v-else-if="blockComponent && isPending" class="space-y-1.5">
    <component
      :is="blockComponent"
      :input="input"
      :state="tool.state"
      :disabled="disabled"
      @submit="onSubmit"
    />
    <div v-if="canSkip" class="flex justify-end">
      <Button type="button" variant="ghost" size="sm" class="text-muted-foreground" @click="onCancel">
        {{ t("skip") }}
      </Button>
    </div>
  </div>
  <ToolPart v-else-if="showToolDetails || !isTerminal" :tool="tool" :text="text" />
</template>

<i18n lang="yaml">
en:
  skip: Skip
  missingBlockId: This chat question is missing the existing assessment block id.
  resourceUnavailable: The current assessment content is not available, so this chat question cannot be answered here.
  blockNotFound: The assessment block "{id}" is not available in this resource.
  blockMismatch: This chat question targets a different assessment block type than the current resource contains.
  notAnswerable: This chat question cannot be answered in this resource.
</i18n>
